import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  Owner,
  RegisterPendingResponse,
  RegisterUserRequest,
  ResetPasswordRequest,
  User,
  AuthStorageData,
} from '../models/models';
import api from './api';

const STORAGE_KEY = 'auth_data';

// ── Helpers localStorage ──────────────────────────────────────────────────────

function saveAuthToStorage(data: AuthStorageData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function loadAuthFromStorage(): AuthStorageData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}


/**
 * Premier utilisateur d'une société → LoginResponse (connexion directe).
 * Sinon → RegisterPendingResponse (compte en attente d'activation par un admin).
 */
export const registerUser = createAsyncThunk<
  LoginResponse | RegisterPendingResponse,
  RegisterUserRequest,
  { rejectValue: string }
>('auth/register-user', async (body, thunkAPI) => {
  try {
    const response = await api.post('/auth/register-user', body);
    return response.data as LoginResponse | RegisterPendingResponse;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || 'Erreur lors de l\'inscription.'
    );
  }
});

export const forgotPassword = createAsyncThunk<
  { message: string },
  ForgotPasswordRequest,
  { rejectValue: string }
>('auth/forgot-password', async (body, thunkAPI) => {
  try {
    const response = await api.post('/auth/forgot-password', body);
    return response.data as { message: string };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || 'Erreur lors de la demande.'
    );
  }
});

export const resetPassword = createAsyncThunk<
  void,
  ResetPasswordRequest,
  { rejectValue: string }
>('auth/reset-password', async (body, thunkAPI) => {
  try {
    await api.post('/auth/reset-password', body);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || 'Token invalide ou expiré.'
    );
  }
});

// ── State ─────────────────────────────────────────────────────────────────────

interface AuthState {
  token: string | null;
  currentUser: Omit<User, 'password'> | null;
  owner: Owner | null;
  permissions: string[];
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  registerMessage: string | null;   // inscription en attente de validation
}

const stored = loadAuthFromStorage();

const initialState: AuthState = {
  token:           stored?.token           ?? null,
  currentUser:     stored?.user            ?? null,
  owner:           stored?.owner           ?? null,
  permissions:     stored?.permissions     ?? [],
  isAuthenticated: !!stored?.token,
  loading:         false,
  error:           null,
  registerMessage: null,
};

// ── Thunks ────────────────────────────────────────────────────────────────────

export const loginUser = createAsyncThunk<
  LoginResponse,
  LoginRequest,
  { rejectValue: string }
>('auth/login', async (credentials, thunkAPI) => {
  try {
    // On appelle directement axios sans l'intercepteur owner_id
    // (l'intercepteur est configuré pour ne pas interférer sur /auth)
    const response = await api.post('/auth/login', credentials);
    return response.data as LoginResponse;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || 'Erreur de connexion.'
    );
  }
});

// ── Slice ─────────────────────────────────────────────────────────────────────

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /** Déconnexion : reset le state Redux. Le nettoyage du localStorage
     *  et autres side-effects (WebSocket, sessionStorage) est centralisé
     *  dans utils/authActions.forceLogout — ne pas dispatch logout() seul. */
    logout(state) {
      state.token           = null;
      state.currentUser     = null;
      state.owner           = null;
      state.permissions     = [];
      state.isAuthenticated = false;
      state.error           = null;
    },
    clearAuthError(state) {
      state.error = null;
    },
    clearRegisterMessage(state) {
      state.registerMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── login ──────────────────────────────────────────────────────────────
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(loginUser.fulfilled, (state, action: PayloadAction<LoginResponse>) => {
        state.loading         = false;
        state.token           = action.payload.token;
        state.currentUser     = action.payload.user;
        state.owner           = action.payload.owner;
        state.permissions     = action.payload.permissions ?? [];
        state.isAuthenticated = true;
        saveAuthToStorage({
          token:       action.payload.token,
          user:        action.payload.user,
          owner:       action.payload.owner,
          permissions: action.payload.permissions ?? [],
        });
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      })
      // ── registerUser ───────────────────────────────────────────────────────
      .addCase(registerUser.pending, (state) => {
        state.loading         = true;
        state.error           = null;
        state.registerMessage = null;
      })
      .addCase(registerUser.fulfilled, (state, action: PayloadAction<LoginResponse | RegisterPendingResponse>) => {
        state.loading = false;
        // Compte en attente d'activation : pas de session ouverte.
        if ('en_attente' in action.payload) {
          state.registerMessage = action.payload.message;
          return;
        }
        // Premier utilisateur de la société : connexion directe.
        state.token           = action.payload.token;
        state.currentUser     = action.payload.user;
        state.owner           = action.payload.owner;
        state.permissions     = action.payload.permissions ?? [];
        state.isAuthenticated = true;
        saveAuthToStorage({
          token:       action.payload.token,
          user:        action.payload.user,
          owner:       action.payload.owner,
          permissions: action.payload.permissions ?? [],
        });
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      })
      // ── forgotPassword ─────────────────────────────────────────────────────
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      })
      // ── resetPassword ──────────────────────────────────────────────────────
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload as string;
      });
  },
});

export const { logout, clearAuthError, clearRegisterMessage } = authSlice.actions;
export default authSlice.reducer;
